import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { ShieldCheck, Plus, Lock, Copy, Users } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", description: "", permissions: [], default_branch_ids: [] };

export default function Roles() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const { data, isLoading } = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data });
  const { data: cat } = useQuery({ queryKey: ["role-perms"], queryFn: async () => (await api.get("/roles/permissions")).data });

  const groups = cat?.groups || [];
  const branches = data?.branches || [];
  const customRoles = data?.custom_roles || [];
  const systemRoles = data?.system_roles || [];

  const reset = () => { setEditing(null); setForm(EMPTY); };
  const save = useMutation({
    mutationFn: async () => {
      if (editing) return api.put(`/roles/${editing.id}`, form);
      return api.post("/roles", form);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["roles"] }); qc.invalidateQueries({ queryKey: ["team"] }); setOpen(false); reset(); toast.success(editing ? "Role updated" : "Role created"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const duplicate = useMutation({
    mutationFn: (id) => api.post(`/roles/${id}/duplicate`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["roles"] }); toast.success("Role duplicated"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const deactivate = useMutation({
    mutationFn: (id) => api.put(`/roles/${id}`, { active: false }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["roles"] }); toast.success("Role deactivated"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const openNew = () => { reset(); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, description: r.description || "", permissions: r.permissions || [], default_branch_ids: r.default_branch_ids || [] }); setOpen(true); };

  const togglePerm = (key) => setForm((f) => ({ ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((x) => x !== key) : [...f.permissions, key] }));
  const toggleGroup = (g) => setForm((f) => {
    const keys = g.permissions.map((p) => p.key);
    const allOn = keys.every((k) => f.permissions.includes(k));
    return { ...f, permissions: allOn ? f.permissions.filter((x) => !keys.includes(x)) : Array.from(new Set([...f.permissions, ...keys])) };
  });
  const toggleBranch = (id) => setForm((f) => ({ ...f, default_branch_ids: f.default_branch_ids.includes(id) ? f.default_branch_ids.filter((x) => x !== id) : [...f.default_branch_ids, id] }));

  if (isLoading) return <Loading />;

  return (
    <div data-testid="roles-page">
      <PageHeader title="Roles" subtitle="System roles & custom tenant roles">
        <Button onClick={openNew} data-testid="role-create-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> Create role</Button>
      </PageHeader>

      {/* System roles */}
      <div className="mb-8">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">System roles</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="system-roles">
          {systemRoles.map((r) => (
            <div key={r.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`system-role-${r.id}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold capitalize text-slate-900">{r.name}</p>
                <Lock className="h-4 w-4 text-slate-300" />
              </div>
              <p className="mt-1 text-xs text-slate-400">{r.permissions?.includes("*") ? "Full access" : `${r.permissions?.length || 0} permissions`}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Custom roles */}
      <div>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">Custom roles</h3>
        {!customRoles.length ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center" data-testid="custom-roles-empty">
            <ShieldCheck className="mx-auto mb-2 h-8 w-8 text-slate-300" />
            <p className="text-sm text-slate-500">No custom roles yet. Build one like "Front Desk" or "Branch Supervisor".</p>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="custom-roles-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">Role</th><th className="px-4 py-3">Permissions</th><th className="px-4 py-3">Users</th><th className="px-4 py-3">Status</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {customRoles.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50" data-testid={`custom-role-row-${r.id}`}>
                    <td className="px-4 py-3"><p className="font-semibold text-slate-900">{r.name}</p>{r.description && <p className="text-xs text-slate-500">{r.description}</p>}</td>
                    <td className="px-4 py-3 text-slate-600">{r.permissions?.length || 0}</td>
                    <td className="px-4 py-3 text-slate-600"><span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5 text-slate-400" />{r.user_count || 0}</span></td>
                    <td className="px-4 py-3">{r.active ? <span className="text-emerald-600 text-xs font-medium">● Active</span> : <span className="text-slate-400 text-xs font-medium">● Inactive</span>}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} data-testid={`role-edit-${r.id}`} className="text-xs font-medium text-slate-700 hover:underline mr-3">Edit</button>
                      <button onClick={() => duplicate.mutate(r.id)} data-testid={`role-duplicate-${r.id}`} className="text-xs font-medium text-slate-500 hover:underline mr-3"><Copy className="inline h-3 w-3" /> Duplicate</button>
                      {r.active && <button onClick={() => { if (r.user_count > 0) { toast.error(`${r.user_count} user(s) assigned. Reassign before deactivating.`); return; } deactivate.mutate(r.id); }} data-testid={`role-deactivate-${r.id}`} className="text-xs font-medium text-rose-600 hover:underline">Deactivate</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader><DialogTitle>{editing ? `Edit role — ${editing.name}` : "Create custom role"}</DialogTitle></DialogHeader>
          {editing && editing.user_count > 0 && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="role-edit-warning">{editing.user_count} user(s) are assigned to this role. Changes affect all of them.</p>
          )}
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input placeholder="Role name (e.g. Front Desk)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="role-name" />
              <Input placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="role-desc" />
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Default branch scope</p>
              <div className="flex flex-wrap gap-3" data-testid="role-branches">
                {branches.map((b) => (
                  <label key={b.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.default_branch_ids.includes(b.id)} onChange={() => toggleBranch(b.id)} data-testid={`role-branch-${b.id}`} />
                    {b.name}
                  </label>
                ))}
                {!branches.length && <p className="text-xs text-slate-400">No branches yet — add in Settings.</p>}
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Permissions</p>
              <div className="space-y-3">
                {groups.map((g) => {
                  const keys = g.permissions.map((p) => p.key);
                  const allOn = keys.every((k) => form.permissions.includes(k));
                  return (
                    <div key={g.module} className="rounded-lg border border-slate-200 p-3" data-testid={`perm-group-${g.module}`}>
                      <div className="mb-2 flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">{g.label}</p>
                        <button onClick={() => toggleGroup(g)} className="text-xs text-slate-500 hover:underline" data-testid={`perm-group-toggle-${g.module}`}>{allOn ? "Clear all" : "Select all"}</button>
                      </div>
                      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                        {g.permissions.map((p) => (
                          <label key={p.key} className="flex items-center gap-2 text-sm text-slate-600">
                            <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => togglePerm(p.key)} data-testid={`perm-${p.key}`} />
                            {p.label}
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="role-cancel">Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name} data-testid="role-save" className="bg-slate-900 text-white">{editing ? "Save changes" : "Create role"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
